import {FC, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import SharedData from '../../common/SharedData'
import {getSettlementSummary, ISettlementSummary} from '../core/_requests'
import {currencyLabel} from '../../common/currencyLabel'

/**
 * Amazon-style "top of the Payments page" summary header.
 *
 * Four cards (in reading order — RTL friendly because the row uses
 * flex/g-3 not absolute positioning):
 *
 *   1. YTD net (paid + pending, with the breakdown underneath)
 *   2. Last paid amount + period — the supplier's most recent payout
 *   3. Next expected payout — earliest in-flight period, with estimated
 *      paid_at date when invoice is uploaded
 *   4. Held — total currently parked in non-terminal statuses, with a
 *      per-status breakdown badge cluster
 *
 * Why this matters: this is the single biggest UX gap with Amazon
 * Seller Central. Today the supplier has to scroll a list to figure
 * out "when will I get paid, and how much?" — these cards answer that
 * in one glance.
 */

type IProps = {
  /** Optional: pin to a specific year (defaults to current calendar year). */
  year?: number
}

const fmtMoney = (
  amount: number,
  currency: string,
  locale: string,
): string => {
  if (!Number.isFinite(amount)) return `0 ${currency}`
  return (
    new Intl.NumberFormat(locale === 'ar' ? 'ar-SA' : 'en-US', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(amount) +
    ' ' +
    currency
  )
}

const fmtDate = (iso: string | null, locale: string): string => {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return d.toLocaleDateString(locale === 'ar' ? 'ar-SA' : 'en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return iso
  }
}

const SettlementSummaryCards: FC<IProps> = ({year}) => {
  const {formatMessage} = useIntl()
  const {currentUser, language} = SharedData()

  const [summary, setSummary] = useState<ISettlementSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [errored, setErrored] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setErrored(false)
      try {
        const res = await getSettlementSummary(
          currentUser?.token,
          language || 'en',
          year,
        )
        if (!cancelled) setSummary(res.data?.data ?? null)
      } catch {
        if (!cancelled) setErrored(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [currentUser?.token, language, year])

  if (loading) {
    return (
      <div className="row g-3 mb-4" aria-busy="true">
        {[0, 1, 2, 3].map((i) => (
          <div className="col-md-6 col-lg-3" key={i}>
            <div className="card card-flush h-100">
              <div className="card-body py-4">
                <div
                  className="placeholder-glow"
                  style={{minHeight: 80}}
                >
                  <span className="placeholder col-7 mb-2 d-block"></span>
                  <span className="placeholder col-5 d-block"></span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (errored || !summary) {
    // Fail-soft — the table below still works on its own. Surfacing
    // a banner instead of crashing the whole page.
    return (
      <div className="alert alert-light fs-7 mb-4" role="status">
        {formatMessage({id: 'settlements.summary.unavailable'})}
      </div>
    )
  }

  // Currency is data-driven (the API reports it per supplier); JOD keeps its translated label so the
  // cards match the table below.
  const c = currencyLabel(summary.currency, formatMessage)
  const ytdTotal = summary.ytd.net_paid + summary.ytd.net_pending

  return (
    <div className="row g-3 mb-4">
      {/* Card 1: YTD net (paid + pending) */}
      <div className="col-md-6 col-lg-3">
        <div className="card card-flush h-100">
          <div className="card-body py-4">
            <div className="text-muted fs-7 fw-semibold">
              {formatMessage(
                {id: 'settlements.card.ytd_title'},
                {year: summary.ytd.year},
              )}
            </div>
            <div className="fs-2hx fw-bold mt-1" style={{lineHeight: 1.1}}>
              {fmtMoney(ytdTotal, c, language)}
            </div>
            <div className="text-muted fs-8 mt-2">
              <span className="text-success fw-semibold">
                {fmtMoney(summary.ytd.net_paid, c, language)}
              </span>{' '}
              {formatMessage({id: 'settlements.card.ytd_paid_suffix'})}
              {' · '}
              <span className="text-warning fw-semibold">
                {fmtMoney(summary.ytd.net_pending, c, language)}
              </span>{' '}
              {formatMessage({id: 'settlements.card.ytd_pending_suffix'})}
            </div>
            <div className="text-muted fs-8 mt-2 border-top pt-2">
              {formatMessage({id: 'settlements.card.ytd_gross_label'})}:{' '}
              <span className="fw-semibold">
                {fmtMoney(summary.ytd.gross_sales, c, language)}
              </span>
              {' · '}
              {formatMessage({id: 'settlements.card.ytd_fees_label'})}:{' '}
              <span className="fw-semibold">
                {fmtMoney(summary.ytd.fees, c, language)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Card 2: Last paid */}
      <div className="col-md-6 col-lg-3">
        <div className="card card-flush h-100">
          <div className="card-body py-4">
            <div className="text-muted fs-7 fw-semibold">
              {formatMessage({id: 'settlements.card.last_paid_title'})}
            </div>
            {summary.last_paid ? (
              <>
                <div
                  className="fs-2hx fw-bold mt-1 text-success"
                  style={{lineHeight: 1.1}}
                >
                  {fmtMoney(summary.last_paid.net, c, language)}
                </div>
                <div className="text-muted fs-8 mt-2">
                  {summary.last_paid.month_name}
                </div>
                {summary.last_paid.invoice_uploaded_at ? (
                  <div className="text-muted fs-8 mt-1">
                    {formatMessage({id: 'settlements.card.invoice_at'})}:{' '}
                    {fmtDate(summary.last_paid.invoice_uploaded_at, language)}
                  </div>
                ) : null}
              </>
            ) : (
              <div className="text-muted fs-7 mt-2">
                {formatMessage({id: 'settlements.card.no_paid_yet'})}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Card 3: Next payout */}
      <div className="col-md-6 col-lg-3">
        <div className="card card-flush h-100">
          <div className="card-body py-4">
            <div className="text-muted fs-7 fw-semibold">
              {formatMessage({id: 'settlements.card.next_payout_title'})}
            </div>
            {summary.next ? (
              <>
                <div
                  className="fs-2hx fw-bold mt-1 text-primary"
                  style={{lineHeight: 1.1}}
                >
                  {fmtMoney(summary.next.net, c, language)}
                </div>
                <div className="text-muted fs-8 mt-2">
                  {summary.next.month_name}
                </div>
                <div className="mt-1">
                  <span className="badge badge-light-warning fs-8">
                    {summary.next.status_text}
                  </span>
                </div>
                <div className="text-muted fs-8 mt-2">
                  {summary.next.estimated_paid_at ? (
                    <>
                      {formatMessage({id: 'settlements.card.estimated_paid_at'})}:{' '}
                      <span className="fw-semibold">
                        {fmtDate(summary.next.estimated_paid_at, language)}
                      </span>
                    </>
                  ) : (
                    formatMessage({id: 'settlements.card.awaiting_invoice'})
                  )}
                </div>
              </>
            ) : (
              <div className="text-muted fs-7 mt-2">
                {formatMessage({id: 'settlements.card.no_pending'})}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Card 4: Held */}
      <div className="col-md-6 col-lg-3">
        <div className="card card-flush h-100">
          <div className="card-body py-4">
            <div className="text-muted fs-7 fw-semibold">
              {formatMessage({id: 'settlements.card.held_title'})}
            </div>
            <div
              className="fs-2hx fw-bold mt-1 text-warning"
              style={{lineHeight: 1.1}}
            >
              {fmtMoney(summary.held.total, c, language)}
            </div>
            <div className="text-muted fs-8 mt-2">
              {formatMessage(
                {id: 'settlements.card.held_across'},
                {count: summary.held.count},
              )}
            </div>
            {summary.held.by_status.length > 0 ? (
              <div className="d-flex flex-wrap gap-1 mt-2">
                {summary.held.by_status.map((b) => (
                  <span
                    key={b.status}
                    className="badge badge-light fs-8"
                    title={fmtMoney(b.total, c, language)}
                  >
                    {b.status_text}: {b.count}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* When this supplier's payment statements are issued, and the next one. */}
      {summary.payment_schedule?.label ? (
        <div className="col-12">
          <div className="text-muted fs-8">
            {formatMessage(
              {id: 'settlements.card.schedule_footnote'},
              {
                schedule: summary.payment_schedule.label,
                date: fmtDate(summary.payment_schedule.next_issue_date, language),
              },
            )}
          </div>
        </div>
      ) : null}

      {/* Payment-terms footnote — explains the "estimated date" math
          so suppliers don't ask why the date changed. */}
      {summary.payment_terms.label ? (
        <div className="col-12">
          <div className="text-muted fs-8">
            {formatMessage(
              {id: 'settlements.card.payment_terms_footnote'},
              {terms: summary.payment_terms.label},
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default SettlementSummaryCards
